import { getToken } from '../utils/authToken';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Crown, Zap, Gift, Star, Sparkles, Shield, Loader2, Image as ImageIcon, MessageCircle, Eye, Layers, Maximize, Wand2, SlidersHorizontal, Save, Download, Moon, History, Search, Heart, Trash2, Archive, LayoutGrid, Workflow, BotMessageSquare, Key, ScanText, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import useEnableBodyScroll from '../hooks/useEnableBodyScroll';
import useScrollDirection from '../hooks/useScrollDirection';
import PricingTOC from './PricingTOC';
import PricingTOCMobile from './PricingTOCMobile';
import PricingProgressBar from './PricingProgressBar';
import BackToTop from './BackToTop';
import VoiceInfoBlock from './blocks/VoiceInfoBlock';
import TransparencyModal from './TransparencyModal';
import { openSubscriptionPanel } from '../utils/openSubscriptionPanel';
import { PREMIUM_BENEFITS } from '../constants/premiumBenefits';
import FreeVsPremiumTable from './pricing/FreeVsPremiumTable';
import LyonHeaderButton from './LyonHeaderButton';
import '../styles/PricingTransparency.css';

const PlanesYBeneficios = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  // Auto-hide del header sticky al scroll-down (estilo Twitter/X). topOffset
  // mas grande que el TOC mobile para que el header se quede un poco mas tiempo.
  const headerVisible = useScrollDirection({ topOffset: 120 });
  const [loadingPeriod, setLoadingPeriod] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Modales de Transparencia (CUADRO NEGRO feb-2026): botón verde sobre la
  // tarjeta del Plan Free → "Qué puede DamyxIA", botón ladrillo sobre la
  // tarjeta del Plan Premium → "Qué NO puede DamyxIA".
  const [showCanModal, setShowCanModal] = useState(false);
  const [showCannotModal, setShowCannotModal] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      const token = getToken();
      if (!token) { setIsAuthenticated(false); return; }
      setIsAuthenticated(true);
      try {
        const response = await axios.get(`${API_URL}/subscriptions/status`, {
        });
        setSubscriptionStatus(response.data);
      } catch {}
    };
    checkStatus();
  }, []);

  // Permitir scroll natural del body solo en esta pagina (el resto de la app
  // usa overflow:hidden para chats con scroll interno). Restaurar al desmontar.
  useEnableBodyScroll();

  // Auto-scroll al bloque Premium si venimos desde el modal global "Funcion Premium"
  useEffect(() => {
    let tries = 0;
    const maxTries = 30;
    const interval = setInterval(() => {
      let flag = null;
      try { flag = sessionStorage.getItem('damyxia_scroll_to_premium'); } catch { /* ignore */ }
      if (flag) {
        const target = document.querySelector('[data-testid="premium-plan-section"]');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          try { sessionStorage.removeItem('damyxia_scroll_to_premium'); } catch { /* ignore */ }
          clearInterval(interval);
          return;
        }
      }
      if (++tries >= maxTries) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const isPremium = subscriptionStatus?.is_premium || false;

  const handleCheckout = async (period) => {
    const token = getToken();
    if (!token) { toast.error(es ? 'Inicia sesion para continuar' : 'Please log in to continue'); navigate('/login'); return; }
    setLoadingPeriod(period);
    try {
      const response = await axios.post(
        `${API_URL}/subscriptions/checkout`,
        { period, origin_url: window.location.origin },
        {}
      );
      if (response.data.checkout_url) {
        toast.success(es ? 'Redirigiendo al pago...' : 'Redirecting to payment...', { duration: 5000 });
        const w = window.open(response.data.checkout_url, '_blank');
        if (!w) window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || (es ? 'Error al crear sesion de pago' : 'Error creating payment session');
      toast.error(typeof msg === 'string' ? msg : (es ? 'Error al crear sesion de pago' : 'Error creating payment session'));
    } finally { setLoadingPeriod(null); }
  };

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">{title}</h3>
      {children}
    </div>
  );

  const ToolItem = ({ icon: Icon, text, color = 'cyan' }) => (
    <div className="flex items-center gap-2.5 py-1.5">
      <div className={`p-1 rounded-md bg-${color}-500/10`}>
        <Icon className={`w-3.5 h-3.5 text-${color}-400`} />
      </div>
      <span className="text-xs text-slate-300">{text}</span>
    </div>
  );

  return (
    <div
      className="min-h-[100dvh] bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-[calc(2rem+env(safe-area-inset-bottom))]"
      data-testid="planes-page-container"
    >
      <PricingProgressBar language={language} />
      <PricingTOC language={language} />
      <PricingTOCMobile language={language} />
      <BackToTop language={language} />
      <header
        className={`sticky top-[3px] z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800
                    transition-transform duration-300 ease-out
                    ${headerVisible ? 'translate-y-0' : '-translate-y-[calc(100%+3px)]'}`}
        data-testid="planes-header"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white transition-colors" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white">{es ? 'Planes, Precios y Beneficios' : 'Plans, Pricing & Benefits'}</h1>
            <p className="text-[10px] text-slate-500">{es ? 'Documento Oficial — Julio 2026' : 'Official Document — July 2026'}</p>
          </div>
          {/* 🟥 P1 feb-2026 — Botón Lyon IA accesible desde /planes para
              que cualquier usuario autenticado pueda abrir el Genio sin
              volver al dashboard. Sólo se monta si hay sesión activa. */}
          {isAuthenticated && (
            <div className="shrink-0">
              <LyonHeaderButton />
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Introduccion */}
        <div className="text-center mb-10">
          <p className="text-sm text-slate-400 max-w-lg mx-auto leading-relaxed">
            {es
              ? 'En DamyxIA creemos en una inteligencia artificial amable, accesible y profundamente humana. Nuestro sistema de planes esta diseñado para ser suave, benevolente y generoso, sin castigos ni bloqueos agresivos.'
              : 'At DamyxIA we believe in kind, accessible and deeply human artificial intelligence. Our plan system is designed to be gentle, benevolent and generous, without punishments or aggressive blocks.'
            }
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-4 sm:gap-6">
            <span className="text-xs text-cyan-400 font-medium">{es ? 'Plan Gratis — accesible y funcional' : 'Free Plan — accessible and functional'}</span>
            <span className="text-xs text-slate-600">|</span>
            <span className="text-xs text-amber-400 font-medium">{es ? 'Plan Premium — completo y profesional' : 'Premium Plan — complete and professional'}</span>
          </div>
        </div>

        {/* Premium Crown State */}
        {isPremium && (
          <div className="flex flex-col items-center justify-center py-6 mb-8 rounded-2xl bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20" data-testid="premium-active-state">
            <div className="relative mb-3">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-2 border-amber-400/40 flex items-center justify-center">
                <Crown className="w-8 h-8 text-amber-400" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            </div>
            <h2 className="text-lg font-bold text-amber-300">{es ? 'Eres Premium' : 'You are Premium'}</h2>
            <p className="text-sm text-slate-400">{es ? 'Disfruta de todas las funciones ilimitadas de DamyxIA' : 'Enjoy all unlimited features of DamyxIA'}</p>
          </div>
        )}

        {/* ===================== PLAN GRATIS ===================== */}
        {/* Botones de Transparencia (par centrado) — FUERA del card, sobre Plan Gratis */}
        <div className="flex flex-wrap justify-center items-center gap-3 mb-3" data-testid="transparency-buttons">
          <button
            type="button"
            className="transparency-button transparency-button--green"
            onClick={() => setShowCanModal(true)}
            data-testid="transparency-can-btn"
          >
            {es ? 'Transparencia: Qué Puede DamyxIA' : 'Transparency: What DamyxIA Can Do'}
          </button>
          <button
            type="button"
            className="transparency-button transparency-button--brick"
            onClick={() => setShowCannotModal(true)}
            data-testid="transparency-cannot-btn"
          >
            {es ? 'Transparencia: Qué NO Puede DamyxIA' : 'Transparency: What DamyxIA Cannot Do'}
          </button>
        </div>
        <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 p-6 mb-6" data-testid="free-plan-section">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <Zap className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{es ? 'Plan Gratis' : 'Free Plan'}</h2>
              <p className="text-xs text-slate-500">{es ? 'Suave, generoso y accesible' : 'Gentle, generous and accessible'}</p>
            </div>
            <span className="ml-auto text-2xl font-bold text-white">$0<span className="text-sm text-slate-500 font-normal ml-1">{es ? 'siempre' : 'forever'}</span></span>
          </div>
          <p className="text-xs text-slate-400 mb-5 mt-2">
            {es ? 'Diseñado para que cualquier persona pueda usar DamyxIA de forma real, util y sin frustraciones.' : 'Designed so anyone can use DamyxIA in a real, useful and frustration-free way.'}
          </p>

          {/* ========================================================= */}
          {/* PLAN FREE — ESPECIFICACION OFICIAL (texto oficial).        */}
          {/* Insertado justo despues del titulo/subtitulo del Plan Free */}
          {/* y ANTES de cualquier otro beneficio. NO MODIFICAR TEXTO.   */}
          {/* ========================================================= */}
          <div
            className="mb-6 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-cyan-500/5 to-slate-900/50 border border-cyan-500/30 p-6"
            data-testid="free-plan-official-block"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                <Zap className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-bold text-white">
                {es ? 'PLAN FREE — LO QUE PUEDES HACER' : 'FREE PLAN — WHAT YOU CAN DO'}
              </h3>
            </div>

            <p className="text-sm text-slate-300 leading-relaxed mb-5">
              {es
                ? 'El Plan Free te permite usar DamyxIA de forma basica para explorar, generar y guardar tus primeras imagenes. Es ideal para probar la plataforma y entender como funciona el flujo creativo.'
                : 'The Free Plan lets you use DamyxIA in a basic way to explore, generate and save your first images. Ideal for trying the platform and understanding how the creative flow works.'}
            </p>

            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              {/* Funciones incluidas */}
              <div className="rounded-xl bg-cyan-500/5 border border-cyan-500/20 p-4">
                <p className="text-xs font-bold text-cyan-300 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" />
                  {es ? 'Funciones incluidas' : 'Included features'}
                </p>
                <ul className="space-y-1.5 text-xs text-slate-200">
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? '8 imagenes mensuales (no acumulables)' : '8 monthly images (non-cumulative)'}</li>
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? 'Papelera visible pero bloqueada' : 'Trash visible but locked'}</li>
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? 'Recuperacion individual: $0.50 por imagen o archivo' : 'Individual recovery: $0.50 per image or file'}</li>
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? 'Papelera Semanal: $3 por 7 dias de acceso ilimitado' : 'Weekly Trash: $3 for 7 days of unlimited access'}</li>
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? 'Retencion 30 dias para imagenes y archivos borrados' : '30-day retention for deleted images and files'}</li>
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? 'Acceso limitado al Historial (solo si tiene items en Papelera)' : 'Limited History (only if there are items in Trash)'}</li>
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? 'Sin thumbnails en Papelera (contenido bloqueado hasta pagar)' : 'No thumbnails in Trash (locked until paid)'}</li>
                  <li className="flex gap-2"><span className="text-cyan-400">•</span>{es ? 'Sin acceso a contenido borrado sin pagar' : 'No access to deleted content without paying'}</li>
                </ul>
              </div>
            </div>

            {/* Compatibilidad */}
            <div className="rounded-xl bg-slate-900/40 border border-slate-700/30 p-4">
              <p className="text-xs font-bold text-slate-300 mb-2 uppercase tracking-wider">
                {es ? 'Compatibilidad' : 'Compatibility'}
              </p>
              <ul className="space-y-1.5 text-xs text-slate-400">
                <li className="flex gap-2"><span className="text-slate-500">•</span>{es ? 'El usuario Free solo puede ver, descargar, marcar favoritas y eliminar imagenes.' : 'Free users can only view, download, favorite and delete images.'}</li>
                <li className="flex gap-2 text-cyan-300/90"><span className="text-cyan-400">→</span>
                  <button
                    type="button"
                    onClick={() => {
                      const target = document.querySelector('[data-testid="premium-plan-section"]');
                      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="text-left bg-transparent p-0 m-0 text-cyan-300/90 hover:text-cyan-200 underline underline-offset-2 decoration-cyan-400/50 hover:decoration-cyan-300 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400/50 rounded"
                    data-testid="free-compat-upgrade-cta"
                  >
                    {es ? 'Para desbloquear funciones avanzadas, debe actualizar al Plan Premium.' : 'To unlock advanced features, upgrade to the Premium Plan.'}
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <Section title={<><ImageIcon className="w-4 h-4 text-cyan-400" /> {es ? 'Generacion Estandar' : 'Standard Generation'}</>}>
              <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/10 mb-3">
                <p className="text-sm text-white font-bold">{es ? '8 imagenes Estandar al mes' : '8 Standard images per month'}</p>
                <p className="text-[10px] text-slate-500">{es ? 'Contador mes calendario. Reinicia el dia 1.' : 'Calendar-month counter. Resets on day 1.'}</p>
              </div>
              <div className="space-y-0.5">
                <ToolItem icon={ImageIcon} text={es ? 'Generacion de imagenes con IA' : 'AI image generation'} />
                <ToolItem icon={Star} text={es ? 'Bonus por referidos: +4 / +4' : 'Referral bonus: +4 / +4'} />
                <ToolItem icon={Save} text={es ? 'Guardado en Historial' : 'History save'} />
              </div>
            </Section>

            <div>
              <Section title={<><MessageCircle className="w-4 h-4 text-cyan-400" /> {es ? 'Mensajes (Total)' : 'Messages (Total)'}</>}>
                <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/10">
                  <p className="text-sm text-white font-bold">{es ? '8 mensajes diarios Total' : '8 Total daily messages'}</p>
                  <ul className="text-[10px] text-slate-500 mt-1 space-y-0.5">
                    <li>{es ? 'Sin restricciones de tipo' : 'No type restrictions'}</li>
                    <li>{es ? 'Sin castigos' : 'No punishments'}</li>
                    <li>{es ? 'Sin bloqueos de herramientas' : 'No tool blocks'}</li>
                    <li>{es ? 'Sin limites ocultos' : 'No hidden limits'}</li>
                  </ul>
                </div>
              </Section>

              <Section title={<><LayoutGrid className="w-4 h-4 text-cyan-400" /> {es ? 'Workspace (Basico)' : 'Workspace (Basic)'}</>}>
                <div className="space-y-1 text-xs text-slate-400">
                  <p>{es ? 'Ver imagenes' : 'View images'}</p>
                  <p>{es ? 'Guardar imagenes' : 'Save images'}</p>
                  <p>{es ? 'Organizar contenido' : 'Organize content'}</p>
                  <p>{es ? 'Notas basicas' : 'Basic notes'}</p>
                </div>
              </Section>

              <Section title={<><Trash2 className="w-4 h-4 text-amber-400" /> {es ? 'Papelera (visible pero bloqueada)' : 'Trash (visible but locked)'}</>}>
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
                  <p className="text-xs text-slate-300 mb-1">{es ? 'Las imagenes borradas se conservan 30 dias. Para recuperarlas:' : 'Deleted images are kept for 30 days. To recover them:'}</p>
                  <p className="text-sm text-amber-400 font-bold">$0.50 USD {es ? '— recuperar una imagen' : '— recover one image'}</p>
                  <p className="text-sm text-amber-400 font-bold">$3.00 USD {es ? '— Papelera Semanal (7 dias ilimitado)' : '— Weekly Trash (7 days unlimited)'}</p>
                  <p className="text-[10px] text-slate-500 mt-2">{es ? 'Pago unico via Stripe Checkout. No se renueva automaticamente.' : 'One-time Stripe Checkout. Does not auto-renew.'}</p>
                </div>
              </Section>

              <Section title={<><Gift className="w-4 h-4 text-amber-400" /> {es ? 'Recompensas por Referidos' : 'Referral Rewards'}</>}>
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                  <p className="text-xs text-slate-300 mb-1">{es ? 'Beneficios oficiales:' : 'Official benefits:'}</p>
                  <p className="text-sm text-amber-400 font-bold">+4 {es ? 'creditos semanales con 3 referidos activos' : 'weekly credits with 3 active referrals'}</p>
                  <p className="text-sm text-amber-400 font-bold">+4 {es ? 'creditos adicionales con 6 referidos activos' : 'extra credits with 6 active referrals'}</p>
                  <p className="text-[10px] text-slate-600 mt-2">{es ? 'El sistema de referidos es 100% interno de DamyxIA. El Pro nunca se regala.' : 'The referral system is 100% internal to DamyxIA. Pro is never given away.'}</p>
                </div>
              </Section>
            </div>
          </div>
        </div>

        {/* ===================== PLAN PREMIUM ===================== */}
        <div className="rounded-2xl bg-gradient-to-b from-amber-500/5 to-slate-800/50 border border-amber-500/30 p-6 mb-6 shadow-lg shadow-amber-500/5" data-testid="premium-plan-section">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <Crown className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Plan Premium</h2>
              <p className="text-xs text-slate-500">{es ? 'Completo, profesional y sin limites' : 'Complete, professional and unlimited'}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-white">$9<span className="text-sm text-slate-400 font-normal">/{es ? 'mes' : 'mo'}</span></p>
              <p className="text-sm text-slate-400 mt-0.5">{es ? 'o' : 'or'}</p>
              <p className="text-2xl font-bold text-white">$86<span className="text-sm text-slate-400 font-normal">/{es ? 'año' : 'year'} <span className="text-xs">({es ? 'pago único' : 'one-time payment'})</span></span></p>
            </div>
          </div>
          <p className="text-xs text-slate-300 italic mb-3 mt-2" data-testid="premium-tagline">
            &ldquo;{es
              ? 'Imágenes ilimitadas. Créditos ilimitados. Mensajes ilimitados.'
              : 'Unlimited images. Unlimited credits. Unlimited messages.'}&rdquo;
          </p>

          {/* Bloque dedicado — cuotas del Documento Oficial */}
          <div className="grid sm:grid-cols-3 gap-3 mb-5" data-testid="premium-quotas">
            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{es ? 'Imágenes (Quick Actions)' : 'Images (Quick Actions)'}</p>
              <p className="text-sm font-bold text-amber-300 mt-1">{es ? 'Ilimitadas' : 'Unlimited'}</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{es ? 'Créditos' : 'Credits'}</p>
              <p className="text-sm font-bold text-amber-300 mt-1">{es ? 'Ilimitados' : 'Unlimited'}</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{es ? 'Mensajes con Lyon-IA' : 'Messages with Lyon-IA'}</p>
              <p className="text-sm font-bold text-amber-300 mt-1">{es ? 'Ilimitados' : 'Unlimited'}</p>
            </div>
          </div>

          {/* 🟥 Cuadro Negro Beneficios Premium oficiales (feb-2026) —
              fuente única `PREMIUM_BENEFITS`. */}
          <ul
            className="grid sm:grid-cols-2 gap-2 mb-5"
            data-testid="planes-premium-benefits-official"
          >
            {PREMIUM_BENEFITS.map((text) => (
              <li key={text} className="flex items-start gap-2 text-sm text-slate-200">
                <Check className="shrink-0 w-4 h-4 mt-0.5 text-amber-300" />
                <span>{text}</span>
              </li>
            ))}
          </ul>

          {/* 🟥 CUADRO NEGRO Panel de Suscripción Único (feb-2026):
              CTA "Comprar Premium" — abre el Panel de Suscripción real
              con los botones $9 y $86 sobre el Área de Trabajo. */}
          <button
            type="button"
            onClick={() => openSubscriptionPanel(navigate)}
            data-testid="open-subscription-panel"
            className="w-full mb-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-zinc-950 bg-gradient-to-r from-amber-400 to-fuchsia-400 hover:from-amber-300 hover:to-fuchsia-300 shadow-lg shadow-amber-500/20 transition-all"
          >
            <Crown className="w-4 h-4" />
            {es ? 'Comprar Premium' : 'Buy Premium'}
          </button>

          {/* VoiceInfoBlock — aclara el alcance actual (STT -> texto) del feature de voz (Premium) */}
          <div className="mb-5" data-testid="voice-info-block-planes-wrapper">
            <VoiceInfoBlock />
          </div>

          {/* ============================================================ */}
          {/* PREMIUM VISION BLOCK — Lectura GPT-4o + Enviar + Title + Tags */}
          {/* ============================================================ */}
          <div
            className="rounded-2xl bg-gradient-to-br from-emerald-500/10 via-cyan-500/5 to-slate-900/50 border border-emerald-500/30 p-6 mb-6"
            data-testid="premium-vision-workspace-block"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <Eye className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2 flex-wrap">
                  {es ? 'FUNCIONES INTELIGENTES DE VISION + WORKSPACE' : 'SMART VISION + WORKSPACE FEATURES'}
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-md">
                    {es ? 'Exclusivo para Premium' : 'Premium Exclusive'}
                  </span>
                </h3>
              </div>
            </div>

            <p className="text-sm text-slate-300 leading-relaxed mb-5">
              {es
                ? 'Subi cualquier imagen con texto — facturas, recibos, formularios, listas, capturas — y DamyxIA la interpreta con GPT-4o Vision. Despues, con un clic, envia el contenido al Area de Trabajo: titulo profesional, tags semanticos y modulo editable, todo automatico.'
                : 'Upload any image with text — invoices, receipts, forms, lists, screenshots — and DamyxIA reads it with GPT-4o Vision. Then, with one click, send the content to the Workspace: professional title, semantic tags and editable module, all automatic.'}
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div className="rounded-xl bg-slate-900/40 border border-emerald-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ScanText className="w-4 h-4 text-emerald-400" />
                  <p className="text-sm font-bold text-emerald-300">
                    {es ? 'Lectura inteligente de imagenes' : 'Smart image reading'}
                  </p>
                </div>
                <ul className="text-[12px] text-slate-300 space-y-1 list-disc list-inside">
                  <li>{es ? 'Texto impreso y manuscrito' : 'Printed and handwritten text'}</li>
                  <li>{es ? 'Reconoce facturas, listas, formularios' : 'Recognizes invoices, lists, forms'}</li>
                  <li>{es ? 'Extrae numeros, fechas, montos, clientes' : 'Extracts numbers, dates, amounts, clients'}</li>
                </ul>
                <p className="text-[10px] text-slate-500 mt-2">
                  {es ? 'Boton "Leer imagen" en el chat.' : '"Read image" button in chat.'}
                </p>
              </div>

              <div className="rounded-xl bg-slate-900/40 border border-emerald-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-4 h-4 text-emerald-400" />
                  <p className="text-sm font-bold text-emerald-300">
                    {es ? 'Boton "Enviar" — un solo clic' : '"Send" button — one click'}
                  </p>
                </div>
                <ul className="text-[12px] text-slate-300 space-y-1 list-disc list-inside">
                  <li>{es ? 'Envia el contenido al Area de Trabajo' : 'Sends content to Workspace'}</li>
                  <li>{es ? 'Abre el editor automaticamente' : 'Opens the editor automatically'}</li>
                  <li>{es ? 'Indicador de procesamiento (3 puntos)' : 'Processing indicator (3 dots)'}</li>
                </ul>
                <p className="text-[10px] text-slate-500 mt-2">
                  {es ? 'Disponible en cada respuesta de DamyxIA.' : 'Available on every DamyxIA reply.'}
                </p>
              </div>

              <div className="rounded-xl bg-slate-900/40 border border-emerald-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <p className="text-sm font-bold text-emerald-300">
                    {es ? 'Titulo inteligente automatico' : 'Smart automatic title'}
                  </p>
                </div>
                <ul className="text-[12px] text-slate-300 space-y-1">
                  <li>"Order #12345 - $99.99" → <span className="text-emerald-300 font-mono">Factura #12345</span></li>
                  <li>"FORMULARIO DE..." → <span className="text-emerald-300 font-mono">Formulario de inscripcion</span></li>
                  <li>"Mouse, Teclado..." → <span className="text-emerald-300 font-mono">Lista de productos</span></li>
                </ul>
              </div>

              <div className="rounded-xl bg-slate-900/40 border border-emerald-500/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <LayoutGrid className="w-4 h-4 text-emerald-400" />
                  <p className="text-sm font-bold text-emerald-300">
                    {es ? 'Tags semanticos automaticos' : 'Automatic semantic tags'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {['factura', '#2024', 'Juan Perez', '$1624', 'productos', 'tecnologia'].map((tg, i) => (
                    <span key={i} className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-700/40">{tg}</span>
                  ))}
                </div>
                <ul className="text-[11px] text-slate-300 space-y-0.5 list-disc list-inside">
                  <li>{es ? 'Buscar por cliente, fecha, monto' : 'Search by client, date, amount'}</li>
                  <li>{es ? 'Filtrar contenido en segundos' : 'Filter content in seconds'}</li>
                </ul>
              </div>
            </div>

            <div className="rounded-xl bg-cyan-500/5 border border-cyan-500/20 p-3 mb-3">
              <p className="text-xs font-bold text-cyan-300 mb-1.5 flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" />
                {es ? 'Persistencia de imagenes en historial' : 'Image persistence in history'}
              </p>
              <p className="text-[12px] text-slate-300">
                {es
                  ? 'Las imagenes que lees con "Leer imagen" se guardan automaticamente en tu historial (conversation_images) con metadata completa. Acceso desde cualquier conversacion sin volver a subirlas.'
                  : 'Images read with "Read image" are automatically saved in your history (conversation_images) with full metadata. Access them from any conversation without re-uploading.'}
              </p>
            </div>

            <div className="rounded-xl bg-slate-900/60 border border-emerald-500/20 p-3">
              <p className="text-xs font-bold text-emerald-300 mb-2">
                {es ? 'Flujo end-to-end (menos de 3 segundos)' : 'End-to-end flow (under 3 seconds)'}
              </p>
              <ol className="text-[12px] text-slate-300 space-y-0.5 list-decimal list-inside">
                <li>{es ? 'Subis una imagen' : 'You upload an image'}</li>
                <li>{es ? 'DamyxIA la lee con GPT-4o' : 'DamyxIA reads it with GPT-4o'}</li>
                <li>{es ? 'Tocas Enviar' : 'You tap Send'}</li>
                <li>{es ? 'Modulo creado en el Area de Trabajo' : 'Module created in Workspace'}</li>
                <li>{es ? 'Titulo y tags generados automaticamente' : 'Title and tags generated automatically'}</li>
                <li>{es ? 'Listo para trabajar' : 'Ready to use'}</li>
              </ol>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Section title={<><Sparkles className="w-4 h-4 text-amber-400" /> {es ? 'IA Avanzada Completa' : 'Complete Advanced AI'}</>}>
                <div className="space-y-0.5">
                  <ToolItem icon={Sparkles} text="Background Removal Pro" color="amber" />
                  <ToolItem icon={Star} text={es ? 'Variantes avanzadas' : 'Advanced Variants'} color="amber" />
                  <ToolItem icon={Maximize} text={es ? 'Upscale avanzado' : 'Advanced Upscale'} color="amber" />
                  <ToolItem icon={Eye} text={es ? 'Vision avanzada' : 'Advanced Vision'} color="amber" />
                  <ToolItem icon={SlidersHorizontal} text={es ? 'Filtros IA avanzados' : 'Advanced AI Filters'} color="amber" />
                </div>
              </Section>
            </div>

            <div>
              <Section title={<><Archive className="w-4 h-4 text-amber-400" /> {es ? 'Exportacion ZIP' : 'ZIP Export'}</>}>
                <div className="space-y-1 text-xs text-slate-400">
                  <p>{es ? 'Descargar todas las imagenes en un archivo ZIP' : 'Download all images in a ZIP file'}</p>
                  <p>{es ? 'Boton directo en el historial' : 'Direct button in history'}</p>
                  <p>{es ? 'Endpoint backend dedicado' : 'Dedicated backend endpoint'}</p>
                </div>
              </Section>

              <Section title={<><Trash2 className="w-4 h-4 text-amber-400" /> {es ? 'Papelera completa' : 'Full Trash access'}</>}>
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <p className="text-sm text-amber-300 font-bold">{es ? 'Acceso ilimitado y sin costo' : 'Unlimited free access'}</p>
                  <ul className="text-[11px] text-slate-300 mt-1.5 space-y-0.5">
                    <li>{es ? '• Recuperaciones ilimitadas' : '• Unlimited recoveries'}</li>
                    <li>{es ? '• Retencion 30 dias' : '• 30-day retention'}</li>
                    <li>{es ? '• Desde Galeria + Historial + Workspace' : '• From Gallery + History + Workspace'}</li>
                  </ul>
                </div>
              </Section>

              <Section title={<><Moon className="w-4 h-4 text-amber-400" /> {es ? 'Modo Oscuro Completo' : 'Full Dark Mode'}</>}>
                <div className="space-y-1 text-xs text-slate-400">
                  <p>{es ? 'Variables CSS globales' : 'Global CSS variables'}</p>
                  <p>{es ? 'Toggle en el header' : 'Toggle in header'}</p>
                  <p>{es ? 'Persistencia automatica' : 'Automatic persistence'}</p>
                </div>
              </Section>

              <Section title={<><LayoutGrid className="w-4 h-4 text-amber-400" /> {es ? 'Workspace Avanzado' : 'Advanced Workspace'}</>}>
                <div className="space-y-0.5">
                  <ToolItem icon={Layers} text={es ? 'Versionado' : 'Versioning'} color="amber" />
                  <ToolItem icon={Workflow} text={es ? 'Pipelines IA' : 'AI Pipelines'} color="amber" />
                  <ToolItem icon={BotMessageSquare} text={es ? 'Automatizaciones' : 'Automations'} color="amber" />
                </div>
              </Section>

              <Section title={<><Shield className="w-4 h-4 text-amber-400" /> {es ? 'Beneficios Adicionales' : 'Additional Benefits'}</>}>
                <div className="space-y-1 text-xs text-slate-400">
                  <p>{es ? 'Prioridad en generacion' : 'Generation priority'}</p>
                  <p>{es ? 'Acceso anticipado a nuevas funciones' : 'Early access to new features'}</p>
                  <p>{es ? 'Meses Premium por referidos' : 'Premium months for referrals'}</p>
                </div>
              </Section>
            </div>
          </div>

          {/* ========================================================= */}
          {/* HISTORIAL AVANZADO — ESPECIFICACION OFICIAL (texto oficial) */}
          {/* Insertado tras "IA Avanzada Completa". Reemplaza al bloque  */}
          {/* corto previo. NO MODIFICAR TEXTO.                          */}
          {/* ========================================================= */}
          <div
            className="mt-6 rounded-2xl bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-slate-900/50 border border-amber-500/30 p-6"
            data-testid="advanced-plan-section"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <History className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  {es ? 'HISTORIAL AVANZADO' : 'ADVANCED HISTORY'}
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-md">
                    {es ? 'Exclusivo para Premium' : 'Premium Exclusive'}
                  </span>
                </h3>
              </div>
            </div>

            <p className="text-sm text-slate-300 leading-relaxed mb-5">
              {es
                ? 'El Historial Avanzado es tu biblioteca personal dentro de DamyxIA. Aqui se guardan todas tus imagenes generadas para que puedas verlas, organizarlas y continuar tu trabajo creativo sin empezar desde cero.'
                : 'Advanced History is your personal library inside DamyxIA. All your generated images are saved here so you can view, organize and continue your creative work without starting from scratch.'}
            </p>

            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              {/* ¿Para qué sirve? */}
              <div className="rounded-xl bg-slate-900/40 border border-slate-700/30 p-4">
                <p className="text-xs font-bold text-amber-300 mb-2 uppercase tracking-wider">
                  {es ? '¿Para que sirve?' : 'What is it for?'}
                </p>
                <ul className="space-y-1.5 text-xs text-slate-300">
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Ver tus imagenes en alta calidad' : 'View your images in high quality'}</li>
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Descargar cualquier imagen cuando quieras' : 'Download any image whenever you want'}</li>
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Marcar favoritas para encontrarlas rapido' : 'Mark favorites to find them fast'}</li>
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Mantener un registro completo de tu progreso creativo' : 'Keep a complete record of your creative progress'}</li>
                </ul>
              </div>

              {/* Funciones avanzadas (solo Premium) */}
              <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4">
                <p className="text-xs font-bold text-amber-300 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                  <Crown className="w-3.5 h-3.5" />
                  {es ? 'Funciones avanzadas (solo Premium)' : 'Advanced features (Premium only)'}
                </p>
                <ul className="space-y-1.5 text-xs text-slate-200">
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Usar cualquier imagen como base para nuevas creaciones' : 'Use any image as a base for new creations'}</li>
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Crear Variantes Pro' : 'Create Pro Variants'}</li>
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Usar imagenes en Fondos y todas las demas funciones avanzadas' : 'Use images in Backgrounds and all other advanced features'}</li>
                  <li className="flex gap-2"><span className="text-amber-400">•</span>{es ? 'Ver y copiar el prompt original de cada imagen' : 'View and copy the original prompt of each image'}</li>
                </ul>
              </div>
            </div>

            {/* ¿Quién puede usarlo? */}
            <div className="rounded-xl bg-slate-900/40 border border-slate-700/30 p-4">
              <p className="text-xs font-bold text-amber-300 mb-3 uppercase tracking-wider">
                {es ? '¿Quien puede usarlo?' : 'Who can use it?'}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg bg-slate-800/40 border border-slate-700/30 p-3">
                  <p className="font-bold text-slate-200 mb-1.5">{es ? 'Usuarios Free' : 'Free users'}</p>
                  <ul className="space-y-1 text-slate-400">
                    <li>{es ? '– Pueden ver, descargar, marcar favoritas y eliminar sus imagenes.' : '– Can view, download, favorite and delete their images.'}</li>
                    <li>{es ? '– NO pueden usar ninguna funcion avanzada del Historial.' : '– CANNOT use any advanced Historial feature.'}</li>
                    <li className="text-amber-300/80">{es ? '– Todas las acciones avanzadas muestran el modal "Funcion Premium".' : '– All advanced actions show the "Premium Feature" modal.'}</li>
                  </ul>
                </div>
                <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3">
                  <p className="font-bold text-amber-300 mb-1.5">{es ? 'Usuarios Premium' : 'Premium users'}</p>
                  <ul className="space-y-1 text-slate-300">
                    <li>{es ? '– Acceso completo a todas las funciones avanzadas del Historial.' : '– Full access to all advanced Historial features.'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Buttons */}
          {!isPremium && isAuthenticated && (
            <div className="flex flex-col gap-3 mt-6 pt-5 border-t border-amber-500/10">
              <button
                onClick={() => handleCheckout('monthly')}
                disabled={loadingPeriod !== null}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold transition-all shadow-lg shadow-amber-500/20 disabled:opacity-70 flex items-center justify-center gap-2 text-sm"
                data-testid="checkout-monthly-btn"
              >
                {loadingPeriod === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                {es ? 'Suscribirse Mensual — $9 USD/mes' : 'Subscribe Monthly — $9 USD/mo'}
              </button>
              <button
                onClick={() => handleCheckout('yearly')}
                disabled={loadingPeriod !== null}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold transition-all shadow-lg shadow-amber-500/20 disabled:opacity-70 flex items-center justify-center gap-2 text-sm"
                data-testid="checkout-yearly-btn"
              >
                {loadingPeriod === 'yearly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                {es ? 'Suscribirse Anual — $86 USD/año (pago único)' : 'Subscribe Yearly — $86 USD/year (one-time payment)'}
              </button>
            </div>
          )}
          {!isAuthenticated && (
            <div className="flex flex-col gap-3 mt-6 pt-5 border-t border-amber-500/10">
              <button onClick={() => navigate('/')} className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold transition-all shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 text-sm">
                <Star className="w-4 h-4" />
                {es ? 'Suscribirse Mensual — $9 USD/mes' : 'Subscribe Monthly — $9 USD/mo'}
              </button>
              <button onClick={() => navigate('/')} className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold transition-all shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 text-sm">
                <Crown className="w-4 h-4" />
                {es ? 'Suscribirse Anual — $86 USD/año (pago único)' : 'Subscribe Yearly — $86 USD/year (one-time payment)'}
              </button>
              <p className="text-center text-xs text-slate-500">{es ? 'Inicia sesion para continuar con el pago' : 'Log in to continue with payment'}</p>
            </div>
          )}
        </div>

        {/* 🟥 CUADRO NEGRO Tabla Free vs Premium (feb-2026) —
            Inmediatamente debajo del bloque Plan Premium y antes del footer. */}
        <FreeVsPremiumTable />

        {/* ===================== PROHIBICIONES ===================== */}
        <div className="rounded-2xl bg-slate-800/20 border border-slate-700/30 p-6 mb-8" data-testid="prohibitions-section">
          <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" />
            {es ? 'Prohibiciones Absolutas (No Modificables)' : 'Absolute Prohibitions (Non-Modifiable)'}
          </h3>
          <p className="text-xs text-slate-500 mb-3">{es ? 'Estas reglas protegen la filosofia suave y benevolente de DamyxIA.' : 'These rules protect the gentle and benevolent philosophy of DamyxIA.'}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {(es ? [
              'No castigar al usuario gratis con bloqueos agresivos.',
              'No reducir la cuota mensual de imagenes Estandar del Plan Free.',
              'No reducir los bonus por referidos (+4 con 3, +4 adic. con 6).',
              'No reducir los 8 mensajes diarios del Plan Gratis.',
              'El Plan Premium siempre es Pago. Nunca entra en Beneficios Gratis.',
            ] : [
              'Do not punish the free user with aggressive blocks.',
              'Do not reduce the monthly Standard image quota of the Free Plan.',
              'Do not reduce referral bonuses (+4 with 3, +4 extra with 6).',
              'Do not reduce the 8 daily messages of the Free Plan.',
              'The Premium Plan is always paid. It never enters Free benefits.',
            ]).map((text, i) => (
              <div key={i} className="flex items-start gap-2 py-1">
                <Shield className="w-3 h-3 text-red-400/60 mt-0.5 shrink-0" />
                <span className="text-xs text-slate-500">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <footer className="pt-4 pb-8 border-t border-slate-700/30">
          <p className="text-center text-slate-600 text-xs">
            {es ? 'DamyxIA — Planes, Precios y Beneficios Oficiales — Abril 2026' : 'DamyxIA — Official Plans, Pricing & Benefits — April 2026'}
          </p>
        </footer>
      </main>

      {/* ============================================================ */}
      {/* CUADRO NEGRO — Modales de Transparencia                       */}
      {/* Botón Verde (Free)    → "Qué Puede DamyxIA"                  */}
      {/* Botón Ladrillo (Pro)  → "Qué NO Puede DamyxIA"               */}
      {/* ============================================================ */}
      {showCanModal && (
        <TransparencyModal
          title={es ? 'Transparencia: Qué Puede DamyxIA Hoy' : 'Transparency: What DamyxIA Can Do Today'}
          onClose={() => setShowCanModal(false)}
          testId="transparency-can-modal"
        >
          <h3>{es ? 'Creación Visual y Contenido' : 'Visual Creation & Content'}</h3>
          <ul>
            <li>{es ? 'Generar imágenes avanzadas con estilos múltiples.' : 'Generate advanced images with multiple styles.'}</li>
            <li>{es ? 'Crear variaciones y mejoras de imágenes existentes.' : 'Create variations and enhance existing images.'}</li>
            <li>{es ? 'Trabajar en un Workspace visual con historial y snapshots.' : 'Work in a visual Workspace with history and snapshots.'}</li>
          </ul>

          <h3>{es ? 'Asistencia Inteligente' : 'Intelligent Assistance'}</h3>
          <ul>
            <li>{es ? 'Conversar y guiarte paso a paso.' : 'Chat and guide you step by step.'}</li>
            <li>{es ? 'Explicar conceptos y ayudarte a tomar decisiones.' : 'Explain concepts and help you make decisions.'}</li>
            <li>{es ? 'Usar un onboarding real (ORB) para adaptarse a tu uso.' : 'Use real onboarding (ORB) to adapt to your usage.'}</li>
          </ul>

          <h3>{es ? 'Gestión de Proyectos Visuales' : 'Visual Project Management'}</h3>
          <ul>
            <li>{es ? 'Organizar tu trabajo en un espacio de proyectos.' : 'Organize your work in a project space.'}</li>
            <li>{es ? 'Guardar versiones y recuperar estados anteriores.' : 'Save versions and recover previous states.'}</li>
            <li>{es ? 'Mantener tu contenido accesible dentro de los límites del plan.' : 'Keep your content accessible within plan limits.'}</li>
          </ul>

          <h3>{es ? 'Monetización Clara' : 'Clear Monetization'}</h3>
          <ul>
            <li>{es ? 'Plan Free con límites transparentes.' : 'Free Plan with transparent limits.'}</li>
            <li>{es ? 'Plan Premium con acceso ampliado y sin costos ocultos.' : 'Premium Plan with extended access and no hidden costs.'}</li>
            <li>{es ? 'Uso controlado y visible para que sepas siempre dónde estás.' : 'Controlled, visible usage so you always know where you stand.'}</li>
          </ul>

          <p>
            {es
              ? 'DamyxIA hoy es una plataforma estable, profesional y enfocada en creación visual, organización y acompañamiento inteligente.'
              : 'Today DamyxIA is a stable, professional platform focused on visual creation, organization and intelligent support.'}
          </p>
        </TransparencyModal>
      )}

      {showCannotModal && (
        <TransparencyModal
          title={es ? 'Transparencia: Qué NO Puede DamyxIA Hoy' : 'Transparency: What DamyxIA Cannot Do Today'}
          onClose={() => setShowCannotModal(false)}
          testId="transparency-cannot-modal"
        >
          <h3>{es ? 'Lo que DamyxIA NO hace (todavía)' : 'What DamyxIA does NOT do (yet)'}</h3>
          <ul>
            <li>{es ? 'No crea apps completas (frontend + backend + base de datos).' : 'It does not build complete apps (frontend + backend + database).'}</li>
            <li>{es ? 'No genera proyectos listos para deploy con dominios y hosting.' : 'It does not generate deploy-ready projects with domains and hosting.'}</li>
            <li>{es ? 'No se auto-modifica ni cambia su propio código en producción.' : 'It does not self-modify or change its own production code.'}</li>
            <li>{es ? 'No ejecuta código arbitrario del usuario en servidores propios.' : 'It does not run arbitrary user code on its own servers.'}</li>
            <li>{es ? 'No crea otras instancias de DamyxIA ni "otra IA" independiente.' : 'It does not spawn other DamyxIA instances or an independent "other AI".'}</li>
          </ul>

          <h3>{es ? 'Alcance Actual' : 'Current Scope'}</h3>
          <ul>
            <li>{es ? 'DamyxIA está enfocada en imágenes, contenido y organización visual.' : 'DamyxIA focuses on images, content and visual organization.'}</li>
            <li>{es ? 'No reemplaza a un equipo de desarrollo completo.' : 'It does not replace a full development team.'}</li>
            <li>{es ? 'No garantiza resultados fuera de su ámbito (por ejemplo, deploy, apps móviles, etc.).' : 'It does not guarantee results outside its scope (e.g. deploys, mobile apps).'}</li>
          </ul>

          <h3>{es ? 'Visión Futura (sin promesas mágicas)' : 'Future Vision (no magic promises)'}</h3>
          <ul>
            <li>{es ? 'Explorar generación de proyectos más completos.' : 'Explore more complete project generation.'}</li>
            <li>{es ? 'Explorar integraciones más profundas con código y despliegue.' : 'Explore deeper code and deploy integrations.'}</li>
            <li>{es ? 'Siempre con transparencia, seguridad y sin exagerar sus capacidades.' : 'Always with transparency, security and no exaggerated claims.'}</li>
          </ul>

          <p>
            {es
              ? 'Preferimos ser claros: DamyxIA no es mágica. Es una herramienta potente de creación visual y asistencia, con límites definidos para evitar falsas expectativas y decepciones.'
              : 'We prefer to be clear: DamyxIA is not magic. It is a powerful tool for visual creation and assistance, with defined limits to avoid false expectations and disappointment.'}
          </p>
        </TransparencyModal>
      )}
    </div>
  );
};

export default PlanesYBeneficios;
